import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  MessagesSquare,
  ArrowLeft,
  Loader2,
  Bot,
  UserCog,
  Lock,
  Clock,
  AlertTriangle,
  RefreshCw,
  Settings,
  X,
  CheckCircle,
  Sparkles,
  MessageSquarePlus,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { useAuth } from "@/contexts/AuthContext";
import { useConversationPresence } from "@/hooks/useConversationPresence";
import { useLocation } from "@/contexts/LocationContext";
import { useGlobalToast } from "@/contexts/ToastContext";
import { useConfirm } from "@/contexts/ConfirmContext";
import { usePermissions } from "@/hooks/usePermissions";
import { extractApiErrorMessage } from "@/lib/api-errors";
import {
  type ConversationMode,
  type ConversationModeFilter,
  type SenderType,
  type ConversationMessageItem,
  type ConversationActionState,
  type ConversationActionName,
  type ConversationSendMessagePayload,
  type ConversationSendMessageErrorBody,
  CONVERSATION_ACTION_ERROR_FALLBACK,
  CONVERSATION_PAGE_SIZE,
  CONVERSATION_LIST_POLL_MS,
  CONVERSATION_DETAIL_POLL_MS,
  formatWaId,
  socialChannelLabel,
  socialChannelStyle,
  type ConversationChannel,
  relativeTime,
  lastMessagePrefix,
  resolveStaffLabel,
  startsNewDay,
  getConversationErrorResponse,
  sendConversationMedia,
  validateConversationMediaFile,
} from "@/lib/conversations";
import ConversationModeBadge from "@/components/ConversationModeBadge";
import ConversationMessageBubble from "@/components/ConversationMessageBubble";
import ConversationDaySeparator from "@/components/ConversationDaySeparator";
import ConversationComposer from "@/components/ConversationComposer";
import { useKeyboardAwareHeight } from "@/hooks/useKeyboardViewport";

/**
 * Inbox de WhatsApp IA (FASE 3b del plan). Lista + chat, patrón
 * mobile-cards/desktop split-pane. Contrato de la API en
 * `Documentos/PLAN_WHATSAPP_IA.md` (FASE 3a, ya desplegada).
 */

// ---------------------------------------------------------------------------
// Tipos (espejo del contrato de `/api/whatsapp-conversations`). `ConversationMode`,
// `SenderType` y `ConversationMessageItem` vienen de `lib/conversations.ts`
// (idénticos en el inbox de plataforma) — aquí solo lo que es propio del
// inquilino (locationId/locationName, ficha de cliente).
// ---------------------------------------------------------------------------

interface ConversationCustomer {
  id: string;
  firstName: string;
  lastName: string;
}

interface ConversationListItem {
  id: string;
  locationId: string | null;
  locationName: string | null;
  waId: string;
  channel?: ConversationChannel;
  contactName?: string | null;
  mode: ConversationMode;
  unreadCount: number;
  lastMessageAt: string | null;
  lastCustomerMessageAt: string | null;
  handoffReason: string | null;
  handoffAt: string | null;
  assignedUserId: string | null;
  createdAt: string;
  customer: ConversationCustomer | null;
  lastMessage: {
    preview: string;
    senderType: SenderType;
    createdAt: string;
  } | null;
}

interface ConversationDetailData {
  id: string;
  locationId: string | null;
  locationName: string | null;
  waId: string;
  channel?: ConversationChannel;
  contactName?: string | null;
  mode: ConversationMode;
  unreadCount: number;
  lastMessageAt: string | null;
  lastCustomerMessageAt: string | null;
  handoffReason: string | null;
  handoffAt: string | null;
  assignedUserId: string | null;
  assignedUserName: string | null;
  createdAt: string;
  customer: ConversationCustomer | null;
  canReplyFreeform: boolean;
}

/** Fila de `GET /add-ons` (solo lo que necesita el candado de esta página). */
interface AddOnListItem {
  code: string;
  name: string;
  price: number;
  subscription: { status: string } | null;
}

/** `GET/PUT /whatsapp-conversations/settings` (timers configurables por sede). */
interface ConversationSettingsData {
  locationId: string;
  locationName: string;
  whatsappHumanTimeoutHours: number;
  /** `null` = no cerrar nunca automáticamente. */
  whatsappAutoCloseDays: number | null;
}

interface LocationOption {
  id: string;
  name: string;
}

// ---------------------------------------------------------------------------
// Helpers propios del inquilino (`formatWaId`, `relativeTime`,
// `lastMessagePrefix`, `resolveStaffLabel` y las constantes de paginación
// viven en `lib/conversations.ts`, compartidos con el inbox de plataforma).
// ---------------------------------------------------------------------------

function customerDisplayName(item: {
  customer: ConversationCustomer | null;
  waId: string;
  channel?: ConversationChannel;
  contactName?: string | null;
}): string {
  if (item.customer) {
    const full = `${item.customer.firstName} ${item.customer.lastName}`.trim();
    if (full) return full;
  }
  // Instagram y Messenger: no hay teléfono, se enseña el nombre de su perfil.
  const social = socialChannelLabel(item.channel);
  if (social) return item.contactName?.trim() || `Cliente de ${social}`;
  return formatWaId(item.waId);
}

/** Etiqueta del canal para los hilos que no son de WhatsApp. */
function SocialChannelBadge({ channel }: { channel?: ConversationChannel }) {
  const style = socialChannelStyle(channel);
  if (!style) return null;
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ${style.chip}`}
    >
      {style.label}
    </span>
  );
}

/** Construye los query params del listado sin claves `undefined` (mismo
 *  criterio que `Customers.tsx`: más seguro que fiarse de cómo cada versión
 *  de axios serializa `undefined`). */
function buildListParams(
  mode: ConversationModeFilter,
  locationId: string,
  page: number,
): Record<string, string | number> {
  const params: Record<string, string | number> = {
    page,
    limit: CONVERSATION_PAGE_SIZE,
  };
  if (mode) params.mode = mode;
  if (locationId) params.locationId = locationId;
  return params;
}

/**
 * Modal "Ajustes" de la página de Conversaciones: los dos timers configurables
 * por sede (`Location.whatsappHumanTimeoutHours` / `whatsappAutoCloseDays`).
 * Gateado en el llamador con `can("sedes:gestionar")` — es configuración de
 * sede, no del permiso operativo `conversaciones:gestionar`.
 */
/**
 * Modal "Nueva conversación" (12-sep-2026): busca un cliente de la
 * organización y le envía la plantilla de apertura desde el número de la
 * sede (POST /whatsapp-conversations/start). Al enviar, abre la conversación
 * en el propio inbox.
 */
function StartConversationModal({
  locations,
  initialLocationId,
  onStarted,
  onClose,
}: {
  locations: LocationOption[];
  initialLocationId: string;
  onStarted: (conversationId: string) => void;
  onClose: () => void;
}) {
  const { showToast } = useGlobalToast();
  const hasMultipleLocations = locations.length > 1;

  const [locationId, setLocationId] = useState(
    initialLocationId || locations[0]?.id || "",
  );
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<
    Array<{ id: string; firstName: string; lastName: string | null; phone: string | null }>
  >([]);
  const [searching, setSearching] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null);

  // Listado inicial + filtro con debounce (petición de Luis, 12-sep-2026:
  // ver la lista de primeras, no solo el buscador).
  useEffect(() => {
    const term = search.trim();
    let active = true;
    setSearching(true);
    const params: Record<string, string | number> = { limit: 15, page: 1 };
    if (term.length >= 2) params.search = term;
    const timer = setTimeout(() => {
      apiClient
        .get<{ success: true; data: Array<{ id: string; firstName: string; lastName: string | null; phone: string | null }> }>(
          "/customers",
          { params },
        )
        .then((res) => {
          if (!active) return;
          setResults(res.data.data ?? []);
        })
        .catch(() => {
          if (active) setResults([]);
        })
        .finally(() => {
          if (active) setSearching(false);
        });
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [search]);

  const handleStart = async (customerId: string) => {
    if (sendingId) return;
    setSendingId(customerId);
    try {
      const res = await apiClient.post("/whatsapp-conversations/start", {
        locationId,
        customerId,
      });
      showToast(res.data?.message || "Mensaje de inicio enviado", "success");
      const conversationId = res.data?.data?.conversationId as string | undefined;
      if (conversationId) onStarted(conversationId);
      onClose();
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "No se pudo iniciar la conversación"),
        "error",
      );
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Nueva conversación
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {hasMultipleLocations && (
            <select
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
            >
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          )}
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Busca un cliente por nombre o teléfono..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
            {searching ? (
              <div className="py-6 flex justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
              </div>
            ) : results.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-400">
                Sin resultados.
              </p>
            ) : (
              results.map((c) => {
                const name = `${c.firstName} ${c.lastName ?? ""}`.trim();
                const canSend = !!c.phone;
                return (
                  <div
                    key={c.id}
                    className="py-2.5 flex items-center justify-between gap-2"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {name || "(sin nombre)"}
                      </p>
                      <p className="text-xs text-gray-500">
                        {c.phone || "Sin teléfono — no se puede escribir"}
                      </p>
                    </div>
                    <button
                      onClick={() => handleStart(c.id)}
                      disabled={!canSend || sendingId !== null}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 flex-shrink-0"
                    >
                      {sendingId === c.id ? "Enviando..." : "Iniciar chat"}
                    </button>
                  </div>
                );
              })
            )}
          </div>
          <p className="text-xs text-gray-400">
            Se le enviará el mensaje de inicio desde el número del negocio y la
            conversación quedará asignada a ti.
          </p>
        </div>
      </div>
    </div>
  );
}

function ConversationSettingsModal({
  locations,
  initialLocationId,
  onClose,
}: {
  locations: LocationOption[];
  initialLocationId: string;
  onClose: () => void;
}) {
  const { showToast } = useGlobalToast();
  const hasMultipleLocations = locations.length > 1;

  const [locationId, setLocationId] = useState(initialLocationId);
  const [locationName, setLocationName] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [timeoutHours, setTimeoutHours] = useState<number | "">(4);
  const [autoCloseDays, setAutoCloseDays] = useState<number | "">("");

  useEffect(() => {
    if (!locationId) {
      setLoading(false);
      setLoadError("Selecciona una sede.");
      return;
    }
    let active = true;
    setLoading(true);
    setLoadError(null);
    apiClient
      .get<{ success: true; data: ConversationSettingsData }>(
        "/whatsapp-conversations/settings",
        { params: { locationId } },
      )
      .then((response) => {
        if (!active) return;
        const data = response.data.data;
        setLocationName(data.locationName);
        setTimeoutHours(data.whatsappHumanTimeoutHours);
        setAutoCloseDays(data.whatsappAutoCloseDays ?? "");
      })
      .catch((err) => {
        if (!active) return;
        setLoadError(
          extractApiErrorMessage(err, "No se pudo cargar la configuración"),
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [locationId]);

  // Igual que `handleCapacityInputChange` de `TableMap.tsx`: se permite el
  // valor vacío mientras el usuario edita, la coacción/validación a número
  // ocurre en `handleSave`.
  const handleTimeoutChange = (raw: string) => {
    if (raw === "") {
      setTimeoutHours("");
      return;
    }
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) return;
    setTimeoutHours(parsed);
  };

  const handleAutoCloseChange = (raw: string) => {
    if (raw === "") {
      setAutoCloseDays("");
      return;
    }
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) return;
    setAutoCloseDays(parsed);
  };

  const handleSave = async () => {
    if (!locationId || saving) return;
    if (timeoutHours === "" || timeoutHours < 1 || timeoutHours > 168) {
      showToast(
        "Las horas para volver al asistente deben estar entre 1 y 168.",
        "error",
      );
      return;
    }
    if (autoCloseDays !== "" && (autoCloseDays < 1 || autoCloseDays > 365)) {
      showToast(
        "Los días para cerrar la conversación deben estar entre 1 y 365 (o vacío para no cerrar nunca).",
        "error",
      );
      return;
    }
    setSaving(true);
    try {
      await apiClient.put("/whatsapp-conversations/settings", {
        locationId,
        whatsappHumanTimeoutHours: timeoutHours,
        whatsappAutoCloseDays: autoCloseDays === "" ? null : autoCloseDays,
      });
      showToast("Configuración guardada", "success");
      onClose();
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "No se pudo guardar la configuración"),
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4">
      <div className="bg-white w-full md:max-w-md md:rounded-xl rounded-t-2xl shadow-xl max-h-[92dvh] flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900">
              Ajustes de conversaciones
            </h3>
            {!hasMultipleLocations && (
              <p className="text-xs text-gray-500 truncate">
                {locationName || locations[0]?.name}
              </p>
            )}
          </div>
          <button onClick={onClose} disabled={saving} className="flex-shrink-0">
            <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {hasMultipleLocations && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sede
              </label>
              <select
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                disabled={saving}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {loading ? (
            <div className="py-8 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
            </div>
          ) : loadError ? (
            <p className="text-sm text-red-600">{loadError}</p>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Volver al asistente tras X horas sin respuesta del equipo
                </label>
                <input
                  type="number"
                  min={1}
                  max={168}
                  step={1}
                  value={timeoutHours}
                  disabled={saving}
                  onChange={(e) => handleTimeoutChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Si el equipo no responde en este tiempo, Carmen retoma la
                  conversación
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cerrar conversaciones tras X días sin actividad
                </label>
                <input
                  type="number"
                  min={1}
                  max={365}
                  step={1}
                  value={autoCloseDays}
                  disabled={saving}
                  onChange={(e) => handleAutoCloseChange(e.target.value)}
                  placeholder="Vacío = nunca"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Vacío = no cerrar nunca. Las cerradas se reabren solas si el
                  cliente vuelve a escribir
                </p>
              </div>
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-end gap-2 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading || !!loadError}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

export default function Conversations() {
  const { user: authUser } = useAuth();
  const { showToast } = useGlobalToast();
  const { confirm } = useConfirm();
  const { can } = usePermissions();
  const canManage = can("conversaciones:gestionar");
  // Ajustes de sede (timers): permiso distinto al operativo de arriba — es
  // configuración de sede (`sedes:gestionar`), no de atención al chat.
  const canManageSettings = can("sedes:gestionar");
  const { locations, currentLocationId } = useLocation();
  // Tiene el complemento contratado pero NINGUNA de sus sedes tiene aún
  // número de WhatsApp: el alta está en marcha.
  const sinNumeroDeWhatsApp =
    locations.length > 0 &&
    locations.every((loc) => !loc.whatsappPhoneNumberId);
  const hasMultipleLocations = locations.length > 1;
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const navigate = useNavigate();
  // Deep-link `?open=<conversationId>` (desde "Iniciar conversación" en el
  // detalle de reserva o la ficha de cliente): abre esa conversación al
  // cargar. Solo se consume una vez.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const open = searchParams.get("open");
    if (open) {
      setSelectedId(open);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Candado de add-on: sin "WhatsApp IA" activo (suelto o vía el pack
  // "Recepcionista IA") la página se sustituye por la pantalla de venta —
  // mismo criterio que la pestaña de la Centralita IA. Si `GET /add-ons`
  // falla se ABRE (granted): un hipo del endpoint jamás debe esconder una
  // función ya pagada.
  const [addonGate, setAddonGate] = useState<
    "checking" | "granted" | "missing"
  >("checking");
  const [addonPrice, setAddonPrice] = useState<number | null>(null);
  // El inbox también lo abre "Redes sociales IA" (Instagram y Messenger). Lo
  // que es SOLO de WhatsApp —el aviso del número en preparación y el botón de
  // iniciar conversación— depende de tener ese complemento, no del inbox.
  const [hasWhatsAppAddon, setHasWhatsAppAddon] = useState(true);
  const esperandoNumero = hasWhatsAppAddon && sinNumeroDeWhatsApp;
  useEffect(() => {
    let active = true;
    apiClient
      .get<{ success: true; data: AddOnListItem[] }>("/add-ons")
      .then((res) => {
        if (!active) return;
        const rows = res.data.data ?? [];
        const whatsappAi = rows.find((a) => a.code === "whatsapp_ai");
        const bundle = rows.find((a) => a.code === "recepcionista_ia");
        const hasWhatsApp =
          whatsappAi?.subscription?.status === "active" ||
          bundle?.subscription?.status === "active";
        const hasSocial =
          rows.find((a) => a.code === "social_ai")?.subscription?.status === "active";
        if (whatsappAi) setAddonPrice(whatsappAi.price);
        setHasWhatsAppAddon(hasWhatsApp);
        setAddonGate(hasWhatsApp || hasSocial ? "granted" : "missing");
      })
      .catch(() => {
        if (active) setAddonGate("granted");
      });
    return () => {
      active = false;
    };
  }, []);

  // Filtros + paginación de la lista
  const [modeFilter, setModeFilter] = useState<ConversationModeFilter>("");
  const [locationFilter, setLocationFilter] = useState("");
  const [page, setPage] = useState(1);
  // Se incrementa para forzar un reintento manual sin cambiar filtros/página
  // (un `setPage` con el mismo valor no dispara el efecto).
  const [listReloadToken, setListReloadToken] = useState(0);

  // Lista
  const [conversations, setConversations] = useState<ConversationListItem[]>(
    [],
  );
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // Selección + detalle
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Chat abierto y a la vista → sin avisos de ese chat (ver el hook).
  useConversationPresence(selectedId);
  // Teclado iOS: la raíz de la página se encoge al área visible mientras se
  // escribe (ver useKeyboardViewport.ts) — sin esto, banda blanca/composer
  // tapado en móvil.
  const pageRootRef = useRef<HTMLDivElement>(null);
  useKeyboardAwareHeight(pageRootRef);
  const [detail, setDetail] = useState<ConversationDetailData | null>(null);
  const [messages, setMessages] = useState<ConversationMessageItem[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailReloadToken, setDetailReloadToken] = useState(0);

  // Caja de respuesta
  const [replyText, setReplyText] = useState("");
  const [attachment, setAttachment] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const handleModeFilterChange = (value: string) => {
    setModeFilter(value as ConversationModeFilter);
    setPage(1);
  };

  const handleLocationFilterChange = (value: string) => {
    setLocationFilter(value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setModeFilter("");
    setLocationFilter("");
    setPage(1);
  };

  const handleSelectConversation = (id: string) => {
    setSelectedId(id);
    setReplyText("");
    setAttachment(null);
    setDetailError(null);
  };

  // ── Lista: fetch + polling (30s), patrón anti-carreras del proyecto
  // (bandera `active` + AbortController propio por petición, ver Dashboard.tsx).
  useEffect(() => {
    // Con la pantalla de venta puesta no hay lista que sondear.
    if (addonGate === "missing") return;
    let active = true;
    let currentController: AbortController | null = null;

    const load = async (opts?: { silent?: boolean }) => {
      currentController?.abort();
      const controller = new AbortController();
      currentController = controller;
      if (!opts?.silent) setListLoading(true);
      try {
        const response = await apiClient.get<{
          success: true;
          data: ConversationListItem[];
          pagination: { page: number; limit: number; total: number; totalPages: number };
        }>("/whatsapp-conversations", {
          params: buildListParams(modeFilter, locationFilter, page),
          signal: controller.signal,
        });
        if (!active) return;
        setConversations(response.data.data);
        setTotal(response.data.pagination.total);
        setTotalPages(response.data.pagination.totalPages);
        setListError(null);
      } catch (err) {
        if (!active || controller.signal.aborted) return;
        setListError(
          extractApiErrorMessage(
            err,
            "No se pudieron cargar las conversaciones",
          ),
        );
      } finally {
        if (active) setListLoading(false);
      }
    };

    load();
    const interval = setInterval(() => load({ silent: true }), CONVERSATION_LIST_POLL_MS);

    return () => {
      active = false;
      currentController?.abort();
      clearInterval(interval);
    };
  }, [modeFilter, locationFilter, page, listReloadToken, addonGate]);

  // ── Detalle: fetch + polling (15s) de la conversación seleccionada.
  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setMessages([]);
      setDetailError(null);
      return;
    }

    let active = true;
    let currentController: AbortController | null = null;

    const load = async (opts?: { silent?: boolean }) => {
      currentController?.abort();
      const controller = new AbortController();
      currentController = controller;
      if (!opts?.silent) setDetailLoading(true);
      try {
        const response = await apiClient.get<{
          success: true;
          data: {
            conversation: Omit<ConversationDetailData, "canReplyFreeform">;
            messages: ConversationMessageItem[];
            canReplyFreeform: boolean;
          };
        }>(`/whatsapp-conversations/${selectedId}`, {
          signal: controller.signal,
        });
        if (!active) return;
        const { conversation, messages: msgs, canReplyFreeform } =
          response.data.data;
        setDetail({ ...conversation, canReplyFreeform });
        setMessages(msgs);
        setDetailError(null);
        // El servidor ya puso unreadCount a 0 al abrir el detalle; se refleja
        // también en la lista sin esperar a que llegue el siguiente poll.
        setConversations((prev) =>
          prev.map((c) =>
            c.id === selectedId ? { ...c, unreadCount: 0 } : c,
          ),
        );
      } catch (err) {
        if (!active || controller.signal.aborted) return;
        setDetailError(
          extractApiErrorMessage(err, "No se pudo cargar la conversación"),
        );
      } finally {
        if (active) setDetailLoading(false);
      }
    };

    load();
    const interval = setInterval(() => load({ silent: true }), CONVERSATION_DETAIL_POLL_MS);

    return () => {
      active = false;
      currentController?.abort();
      clearInterval(interval);
    };
  }, [selectedId, detailReloadToken]);

  // Scroll al fondo al abrir una conversación o al llegar mensajes nuevos.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [selectedId, messages.length]);

  // ── Aplica el estado devuelto por take-over / return-to-bot / close a la
  // lista y al detalle a la vez, sin esperar al siguiente poll.
  const applyActionState = useCallback(
    (state: ConversationActionState) => {
      setDetail((prev) => {
        if (!prev || prev.id !== state.id) return prev;
        const assignedUserName =
          state.assignedUserId === null
            ? null
            : state.assignedUserId === authUser?.id
              ? `${authUser?.firstName ?? ""} ${authUser?.lastName ?? ""}`.trim() ||
                prev.assignedUserName
              : prev.assignedUserName;
        return {
          ...prev,
          mode: state.mode,
          assignedUserId: state.assignedUserId,
          assignedUserName,
          handoffReason: state.handoffReason,
          handoffAt: state.handoffAt,
          unreadCount: state.unreadCount,
          lastMessageAt: state.lastMessageAt,
          lastCustomerMessageAt: state.lastCustomerMessageAt,
          canReplyFreeform: state.canReplyFreeform,
        };
      });
      setConversations((prev) =>
        prev.map((c) =>
          c.id === state.id
            ? {
                ...c,
                mode: state.mode,
                assignedUserId: state.assignedUserId,
                handoffReason: state.handoffReason,
                handoffAt: state.handoffAt,
                unreadCount: state.unreadCount,
                lastMessageAt: state.lastMessageAt,
                lastCustomerMessageAt: state.lastCustomerMessageAt,
              }
            : c,
        ),
      );
    },
    [authUser],
  );

  const runAction = useCallback(
    async (action: ConversationActionName, confirmFirst?: () => Promise<boolean>) => {
      if (!selectedId || actionLoading) return;
      if (confirmFirst) {
        const ok = await confirmFirst();
        if (!ok) return;
      }
      setActionLoading(true);
      try {
        const response = await apiClient.post<{
          success: true;
          message: string;
          data: ConversationActionState;
        }>(`/whatsapp-conversations/${selectedId}/${action}`);
        applyActionState(response.data.data);
        showToast(response.data.message, "success");
      } catch (err) {
        showToast(
          extractApiErrorMessage(err, CONVERSATION_ACTION_ERROR_FALLBACK[action]),
          "error",
        );
      } finally {
        setActionLoading(false);
      }
    },
    [selectedId, actionLoading, applyActionState, showToast],
  );

  const handleTakeOver = useCallback(
    () => runAction("take-over"),
    [runAction],
  );
  const handleReturnToBot = useCallback(
    () => runAction("return-to-bot"),
    [runAction],
  );
  // "Que conteste la IA": responde YA a lo pendiente y se queda el control
  // (distinto de "Devolver al bot", que es silencioso). Tras el éxito se
  // fuerza una recarga del detalle con un pequeño retraso para pintar la
  // respuesta del bot sin esperar al poll de 15s.
  // Reabrir la ventana de 24h: envía la plantilla de apertura a ESTE cliente
  // desde el número del negocio. La ventana de texto libre no se abre hasta
  // que el cliente responda (regla de Meta), pero el mensaje ya viaja.
  const [reopening, setReopening] = useState(false);
  const handleReopenWindow = useCallback(async () => {
    if (!selectedId || reopening) return;
    setReopening(true);
    try {
      const res = await apiClient.post("/whatsapp-conversations/start", {
        conversationId: selectedId,
      });
      showToast(
        res.data?.message || "Mensaje de inicio enviado. En cuanto responda podrás escribirle libremente.",
        "success",
      );
      setDetailReloadToken((t) => t + 1);
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "No se pudo enviar el mensaje de inicio"),
        "error",
      );
    } finally {
      setReopening(false);
    }
  }, [selectedId, reopening, showToast]);

  const handleAnswerWithAi = useCallback(async () => {
    await runAction("answer-with-ai");
    setTimeout(() => setDetailReloadToken((t) => t + 1), 1200);
  }, [runAction]);
  const handleClose = useCallback(
    () =>
      runAction("close", () =>
        confirm({
          title: "Cerrar conversación",
          message:
            "El cliente dejará de aparecer en el inbox de atención humana. Si vuelve a escribir por WhatsApp, la conversación se reabre automáticamente en modo bot.",
          confirmText: "Cerrar conversación",
          cancelText: "Cancelar",
          danger: true,
        }),
      ),
    [runAction, confirm],
  );

  // ── Enviar respuesta. Sin optimismo: se añade el mensaje que devuelve el
  // servidor. En 502 (Meta rechazó el envío) el mensaje SÍ quedó guardado —
  // se añade igual, marcado como no entregado.
  const appendSentMessage = useCallback(
    (payload: ConversationSendMessagePayload, notDelivered: boolean) => {
      setMessages((prev) => [
        ...prev,
        notDelivered ? { ...payload.message, notDelivered: true } : payload.message,
      ]);
      setDetail((prev) => {
        if (!prev || prev.id !== payload.conversation.id) return prev;
        const assignedUserName =
          payload.conversation.assignedUserId === authUser?.id
            ? `${authUser?.firstName ?? ""} ${authUser?.lastName ?? ""}`.trim() ||
              prev.assignedUserName
            : prev.assignedUserName;
        return {
          ...prev,
          mode: payload.conversation.mode,
          assignedUserId: payload.conversation.assignedUserId,
          assignedUserName,
          lastMessageAt: payload.message.createdAt,
        };
      });
      setConversations((prev) =>
        prev.map((c) =>
          c.id === payload.conversation.id
            ? {
                ...c,
                mode: payload.conversation.mode,
                assignedUserId: payload.conversation.assignedUserId,
                unreadCount: 0,
                lastMessageAt: payload.message.createdAt,
                lastMessage: {
                  preview: payload.message.body.slice(0, 80),
                  senderType: payload.message.senderType,
                  createdAt: payload.message.createdAt,
                },
              }
            : c,
        ),
      );
    },
    [authUser],
  );

  const handleSendMessage = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const text = replyText.trim();
      if ((!text && !attachment) || !selectedId || sending) return;
      if (attachment && text.length > 1024) {
        showToast(
          "El texto que acompaña al archivo no puede superar 1024 caracteres.",
          "error",
        );
        return;
      }
      setSending(true);
      try {
        const response = attachment
          ? await sendConversationMedia(`/whatsapp-conversations/${selectedId}`, attachment, text)
          : await apiClient.post<{
              success: true;
              message: string;
              data: ConversationSendMessagePayload;
            }>(`/whatsapp-conversations/${selectedId}/messages`, { body: text });
        appendSentMessage(response.data.data, false);
        setReplyText("");
        setAttachment(null);
      } catch (err) {
        const errResponse = getConversationErrorResponse(err);
        const body = errResponse?.data as ConversationSendMessageErrorBody | undefined;
        if (errResponse?.status === 502 && body?.data) {
          appendSentMessage(body.data, true);
          setReplyText("");
          setAttachment(null);
          showToast(
            body.message || "WhatsApp no ha aceptado el mensaje.",
            "error",
          );
        } else {
          showToast(
            extractApiErrorMessage(err, "No se pudo enviar el mensaje"),
            "error",
          );
        }
      } finally {
        setSending(false);
      }
    },
    [replyText, attachment, selectedId, sending, appendSentMessage, showToast],
  );

  /** Clip del composer: valida formato y tamaño antes de dejarlo adjunto. */
  const handleAttachmentChange = useCallback(
    (file: File | null) => {
      const error = file ? validateConversationMediaFile(file) : null;
      if (error) {
        showToast(error, "error");
        return;
      }
      setAttachment(file);
    },
    [showToast],
  );

  const noFiltersActive = modeFilter === "" && locationFilter === "";

  // ── Candado de add-on: comprobando → spinner (evita el destello del inbox
  // vacío); sin add-on → pantalla de venta (calco del patrón de VoiceAI.tsx).
  if (addonGate === "checking") {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }
  if (addonGate === "missing") {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
          <div className="text-center max-w-md mx-auto">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessagesSquare className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              WhatsApp IA
            </h2>
            <p className="text-gray-500 text-sm mb-6">
              <strong>Carmen</strong>, la misma inteligencia artificial de la
              Centralita, atendiendo el WhatsApp de tu negocio desde su propio
              número — y este inbox para que tu equipo vea cada conversación y
              tome el control cuando haga falta.
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6 text-left">
              <h3 className="text-sm font-medium text-emerald-900 mb-2">
                ¿Qué hace WhatsApp IA?
              </h3>
              <ul className="text-sm text-emerald-800 space-y-1">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  Crea, consulta, modifica y cancela reservas o citas por chat
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  Reconoce a cada cliente por su número de teléfono
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  Ofrece tus menús con listas interactivas de WhatsApp
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  Avisa a tu equipo y le pasa la conversación si se atasca
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  Atiende 24/7, también cuando el local está cerrado
                </li>
              </ul>
            </div>
            <p className="text-gray-400 text-xs mb-6">
              Complemento{addonPrice !== null ? ` de ${addonPrice}€/mes` : ""}{" "}
              sobre tu plan de pago. También incluido en el pack Recepcionista
              IA junto a la Centralita, con el mismo número para voz y chat.
            </p>
            <button
              onClick={() => navigate("/subscription")}
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
            >
              <Sparkles className="w-5 h-5" />
              Contratar WhatsApp IA
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Altura definida en toda la cadena (lección 18-ago-2026): en móvil la
  // columna visible (lista o chat, se alternan con hidden/flex) debe poder
  // CRECER para llenar el alto fijo del contenedor raíz — `flex-1 min-h-0`,
  // no solo `flex-shrink-0` (eso deja la altura en "auto" = tamaño de
  // contenido, la lista entera se renderiza fuera del viewport y el scroll
  // interno del `overflow-y-auto` nunca dispara). En desktop (md+) la lista
  // vuelve a su ancho fijo de columna (`flex-none`, igual que antes).
  const listColumnClasses = `${
    selectedId ? "hidden md:flex" : "flex"
  } w-full md:w-80 lg:w-96 flex-1 md:flex-none min-h-0 flex-col border-r border-gray-200 bg-white overflow-hidden`;
  const chatColumnClasses = `${
    selectedId ? "flex" : "hidden md:flex"
  } flex-1 min-h-0 flex-col overflow-hidden bg-gray-50`;

  return (
    // `100dvh` en móvil (no `100vh`): en iOS Safari `100vh` se calcula con la
    // barra de URL oculta (viewport "grande", valor estático) — con la barra
    // visible el documento queda más alto que lo realmente visible y el
    // navegador ofrece scroll de página para revelar el resto, dejando la
    // caja de respuesta por debajo del fold. `100dvh` seguimos el viewport
    // visible real. En desktop (md+) se mantiene `100vh` tal cual (sin cambio
    // de comportamiento, ahí no hay barra de URL dinámica).
    <div
      ref={pageRootRef}
      className="flex-1 min-h-0 flex flex-col md:flex-row bg-gray-50 overflow-hidden -m-4 lg:-m-8"
    >
      {/* ── Lista ── */}
      <div className={listColumnClasses}>
        <div className="flex-shrink-0 border-b border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2 mb-3">
            <MessagesSquare className="w-5 h-5 text-primary-600 flex-shrink-0" />
            <h1 className="text-lg font-bold text-gray-900 flex-1">
              Conversaciones
            </h1>
            {canManage && hasWhatsAppAddon && locations.length > 0 && (
              <button
                onClick={() => setStartOpen(true)}
                className="p-1.5 rounded-lg text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 flex-shrink-0 transition-colors"
                title="Nueva conversación: enviar el mensaje de inicio a un cliente"
              >
                <MessageSquarePlus className="w-4 h-4" />
              </button>
            )}
            {canManageSettings && locations.length > 0 && (
              <button
                onClick={() => setSettingsOpen(true)}
                className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 flex-shrink-0 transition-colors"
                title="Ajustes de conversaciones"
              >
                <Settings className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <select
              value={modeFilter}
              onChange={(e) => handleModeFilterChange(e.target.value)}
              className="flex-1 min-w-0 border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
            >
              <option value="">Todas</option>
              <option value="bot">Bot</option>
              <option value="human">Atención humana</option>
              <option value="closed">Cerradas</option>
            </select>
            {hasMultipleLocations && (
              <select
                value={locationFilter}
                onChange={(e) => handleLocationFilterChange(e.target.value)}
                className="flex-1 min-w-0 border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
              >
                <option value="">Todas las sedes</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Contratado pero sin número todavía: el alta la aprueba WhatsApp y
            puede tardar horas. Sin este aviso el cliente solo veía un inbox
            vacío y no sabía si había pagado para nada (23-sep-2026). */}
        {esperandoNumero && (
          <div className="mx-3 mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
            <p className="text-sm font-medium text-amber-900">
              Estamos preparando tu número de WhatsApp
            </p>
            <p className="text-xs text-amber-800 mt-0.5">
              El alta puede tardar hasta 24 horas porque la aprueba WhatsApp. No
              tienes que hacer nada: te avisamos por email en cuanto esté listo.
            </p>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {listLoading ? (
            <div className="p-12 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
            </div>
          ) : listError ? (
            <div className="p-6 text-center">
              <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-sm text-red-600 mb-3">{listError}</p>
              <button
                onClick={() => setListReloadToken((t) => t + 1)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:underline"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Reintentar
              </button>
            </div>
          ) : conversations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <MessagesSquare className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              {noFiltersActive ? (
                <>
                  <p className="font-medium text-gray-700">
                    Todavía no hay conversaciones
                  </p>
                  {/* El texto viejo decía "actívalo desde tu gestor de
                      cuenta, de momento no hay pantalla de contratación":
                      contradecía a la pantalla de venta que sí existe y, a
                      quien ya lo tiene contratado, le hacía dudar de si está
                      activo (23-sep-2026). */}
                  <p className="text-sm mt-1">
                    Aquí verás las conversaciones de WhatsApp de Carmen con tus
                    clientes, con la opción de tomar el control cuando haga
                    falta. En cuanto alguien escriba a tu número, aparecerán
                    solas.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm">No hay conversaciones con este filtro.</p>
                  <button
                    onClick={handleClearFilters}
                    className="mt-2 text-sm font-medium text-primary-600 hover:underline"
                  >
                    Quitar filtros
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {conversations.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleSelectConversation(item.id)}
                  className={`w-full text-left p-3 md:p-4 flex items-start gap-2 min-w-0 hover:bg-gray-50 transition-colors ${
                    selectedId === item.id ? "bg-primary-50" : ""
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-gray-900 truncate">
                        {customerDisplayName(item)}
                      </p>
                      <span className="flex-shrink-0 text-xs text-gray-400">
                        {relativeTime(item.lastMessageAt ?? item.createdAt)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <ConversationModeBadge mode={item.mode} />
                      <SocialChannelBadge channel={item.channel} />
                      {hasMultipleLocations && item.locationName && (
                        <span className="text-xs text-gray-400 truncate">
                          {item.locationName}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 truncate mt-1">
                      {item.lastMessage
                        ? `${lastMessagePrefix(item.lastMessage.senderType)}${item.lastMessage.preview}`
                        : "Sin mensajes todavía"}
                    </p>
                  </div>
                  {item.unreadCount > 0 && (
                    <span className="flex-shrink-0 mt-0.5 min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-semibold flex items-center justify-center">
                      {item.unreadCount > 99 ? "99+" : item.unreadCount}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex-shrink-0 flex items-center justify-between gap-2 border-t border-gray-200 px-3 py-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <span className="text-xs text-gray-500">
              Página {page} de {totalPages} · {total}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Siguiente
            </button>
          </div>
        )}
      </div>

      {/* ── Chat ── */}
      <div className={chatColumnClasses}>
        {!selectedId ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center text-gray-400 p-8">
            <MessagesSquare className="w-12 h-12 mb-3" />
            <p>Selecciona una conversación para ver los mensajes.</p>
          </div>
        ) : detailLoading && !detail ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : detailError && !detail ? (
          <div className="flex-1 flex items-center justify-center p-6 text-center">
            <div>
              <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-sm text-red-600">{detailError}</p>
            </div>
          </div>
        ) : detail ? (
          <>
            {/* Cabecera */}
            <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3">
              <div className="flex items-start gap-2">
                <button
                  className="md:hidden p-1.5 -ml-1.5 hover:bg-gray-100 rounded-lg flex-shrink-0"
                  onClick={() => setSelectedId(null)}
                  title="Volver a la lista"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-semibold text-gray-900 truncate">
                      {customerDisplayName(detail)}
                    </h2>
                    <ConversationModeBadge mode={detail.mode} />
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5 text-xs text-gray-500 flex-wrap">
                    <span>
                      {socialChannelLabel(detail.channel) ?? formatWaId(detail.waId)}
                    </span>
                    {detail.locationName && (
                      <>
                        <span>·</span>
                        <span className="truncate">{detail.locationName}</span>
                      </>
                    )}
                    {detail.assignedUserName && (
                      <>
                        <span>·</span>
                        <span className="truncate">
                          Asignada a {detail.assignedUserName}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {canManage && (
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  {(detail.mode === "bot" || detail.mode === "closed") && (
                    <button
                      onClick={handleTakeOver}
                      disabled={actionLoading}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-50 text-primary-700 hover:bg-primary-100 disabled:opacity-50 transition-colors"
                    >
                      <UserCog className="w-3.5 h-3.5" /> Tomar el control
                    </button>
                  )}
                  {detail.mode === "human" && (
                    <>
                      <button
                        onClick={handleAnswerWithAi}
                        disabled={actionLoading}
                        title="La IA responde ahora a lo pendiente y continúa ella con la conversación"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 transition-colors"
                      >
                        <Sparkles className="w-3.5 h-3.5" /> Que conteste la IA
                      </button>
                      <button
                        onClick={handleReturnToBot}
                        disabled={actionLoading}
                        title="Devuelve el control al bot sin responder nada ahora"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50 transition-colors"
                      >
                        <Bot className="w-3.5 h-3.5" /> Devolver al bot
                      </button>
                    </>
                  )}
                  {detail.mode !== "closed" && (
                    <button
                      onClick={handleClose}
                      disabled={actionLoading}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50 transition-colors"
                    >
                      <Lock className="w-3.5 h-3.5" /> Cerrar
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Mensajes */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <p className="text-center text-sm text-gray-400 mt-8">
                  Todavía no hay mensajes en esta conversación.
                </p>
              ) : (
                messages.map((msg, index) => (
                  <Fragment key={msg.id}>
                    {startsNewDay(msg.createdAt, messages[index - 1]?.createdAt) && (
                      <ConversationDaySeparator createdAt={msg.createdAt} />
                    )}
                    <ConversationMessageBubble
                      message={msg}
                      mediaBasePath={`/whatsapp-conversations/${detail.id}/media`}
                      customerLabel={customerDisplayName(detail)}
                      staffLabel={(m) =>
                        resolveStaffLabel(m, detail, authUser?.id)
                      }
                    />
                  </Fragment>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Caja de respuesta */}
            {!canManage ? (
              <div className="flex-shrink-0 bg-white border-t border-gray-200 p-3 text-center text-sm text-gray-500">
                Solo lectura: no tienes permiso para responder en esta
                conversación.
              </div>
            ) : detail.mode === "closed" ? (
              <div className="flex-shrink-0 bg-gray-50 border-t border-gray-200 p-3">
                <textarea
                  disabled
                  rows={2}
                  placeholder="Esta conversación está cerrada"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-100 text-gray-400 resize-none cursor-not-allowed"
                />
                <div className="flex items-center justify-between gap-3 flex-wrap mt-2">
                  <p className="text-sm text-gray-500">
                    Si el cliente vuelve a escribir, se reabre sola en modo
                    bot.
                  </p>
                  <button
                    onClick={handleTakeOver}
                    disabled={actionLoading}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition-colors flex-shrink-0"
                  >
                    Reabrir (tomar el control)
                  </button>
                </div>
              </div>
            ) : !detail.canReplyFreeform ? (
              <div className="flex-shrink-0 bg-amber-50 border-t border-amber-200 p-3">
                <textarea
                  disabled
                  rows={2}
                  placeholder={`Fuera de la ventana de 24h de ${socialChannelLabel(detail.channel) ?? "WhatsApp"}`}
                  className="w-full px-3 py-2 border border-amber-200 rounded-lg text-sm bg-amber-100/60 text-amber-700 placeholder-amber-500 resize-none cursor-not-allowed"
                />
                <div className="flex items-center justify-between gap-3 flex-wrap mt-2">
                  <p className="flex items-center gap-2 text-sm text-amber-700">
                    <Clock className="w-4 h-4 flex-shrink-0" />
                    {socialChannelLabel(detail.channel)
                      ? `Han pasado más de 24h desde su último mensaje. En ${socialChannelLabel(detail.channel)} solo se puede contestar dentro de ese plazo: hay que esperar a que vuelva a escribir.`
                      : "Han pasado más de 24h desde su último mensaje. Envíale el mensaje de inicio para retomar el contacto."}
                  </p>
                  {canManage && !socialChannelLabel(detail.channel) && (
                    <button
                      onClick={handleReopenWindow}
                      disabled={reopening}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors flex-shrink-0"
                    >
                      {reopening ? "Enviando..." : "Enviar mensaje de inicio"}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <ConversationComposer
                value={replyText}
                onChange={setReplyText}
                onSubmit={handleSendMessage}
                sending={sending}
                attachment={attachment}
                onAttachmentChange={
                  socialChannelLabel(detail.channel) ? undefined : handleAttachmentChange
                }
              />
            )}
          </>
        ) : null}
      </div>

      {startOpen && (
        <StartConversationModal
          locations={locations.map((l) => ({ id: l.id, name: l.name }))}
          initialLocationId={locationFilter || currentLocationId || ""}
          onStarted={(conversationId) => {
            setSelectedId(conversationId);
            setListReloadToken((t) => t + 1);
          }}
          onClose={() => setStartOpen(false)}
        />
      )}
      {settingsOpen && (
        <ConversationSettingsModal
          locations={locations}
          initialLocationId={
            locations.length === 1
              ? locations[0].id
              : locationFilter ||
                currentLocationId ||
                locations[0]?.id ||
                ""
          }
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </div>
  );
}
